"use client";

import Link from "@/components/locale-link";
import { t } from "@/lib/i18n/interpolate";
import {
  ASSIGNMENT_LINKS,
  type AssignmentLink,
  type PlannedStep,
} from "@/lib/tool-kit/study/assignment-plan";

/*
 * The assignment planner's results: a bar showing how the working days are shared, and
 * one card per step. Every word comes in through `labels` and the describe functions,
 * from the widget's strings.
 */

const TIMELINE_COLORS = [
  "bg-primary/80",
  "bg-primary/45",
  "bg-primary/65",
  "bg-primary/30",
];

export interface StepDescriber {
  name: (step: PlannedStep) => string;
  tip: (step: PlannedStep) => string;
  /** "Mon, Oct 6 – Fri, Oct 9", or one date. */
  range: (step: PlannedStep) => string;
  /** "4 working days", or that the step shares its day. */
  days: (step: PlannedStep) => string;
  finishBy: (step: PlannedStep) => string;
  share: (step: PlannedStep) => string;
}

export function PlanTimeline({
  plan,
  totalDays,
  label,
  name,
}: {
  plan: readonly PlannedStep[];
  totalDays: number;
  label: string;
  name: (step: PlannedStep) => string;
}) {
  return (
    <div
      aria-label={label}
      className="flex h-3 w-full overflow-hidden rounded-full bg-muted print:hidden"
      role="img"
    >
      {plan.map((step, index) =>
        step.days > 0 ? (
          <div
            className={TIMELINE_COLORS[index % TIMELINE_COLORS.length]}
            key={step.key}
            style={{ width: `${(step.days / totalDays) * 100}%` }}
            title={name(step)}
          />
        ) : null
      )}
    </div>
  );
}

export function PlanSteps({
  plan,
  describe,
  stepHeading,
  useLabel,
  linkLabels,
}: {
  plan: readonly PlannedStep[];
  describe: StepDescriber;
  /** "{number}. {name}" */
  stepHeading: string;
  useLabel: string;
  linkLabels: Readonly<Record<AssignmentLink, string>>;
}) {
  return (
    <ol className="space-y-3">
      {plan.map((step, index) => (
        <li
          className="space-y-2 rounded-lg border border-border bg-card p-4 print:break-inside-avoid print:rounded-none print:border-x-0 print:border-t-0 print:px-0"
          key={step.key}
        >
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <h3 className="font-semibold text-base text-foreground">
              {t(stepHeading, { number: index + 1, name: describe.name(step) })}
            </h3>
            <p className="font-medium text-primary text-sm print:text-foreground">
              {describe.finishBy(step)}
            </p>
          </div>
          <p className="text-muted-foreground text-xs">
            {describe.share(step)}
            {" · "}
            {describe.days(step)}
            {" · "}
            {describe.range(step)}
          </p>
          <p className="text-foreground text-sm leading-relaxed">
            {describe.tip(step)}
          </p>
          {step.links.length > 0 ? (
            <p className="text-sm">
              <span className="text-muted-foreground">{useLabel}</span>{" "}
              {step.links.map((link, linkIndex) => (
                <span key={link}>
                  {linkIndex > 0 ? " · " : null}
                  <Link
                    className="font-medium text-primary underline-offset-2 hover:underline"
                    href={ASSIGNMENT_LINKS[link]}
                  >
                    {linkLabels[link]}
                  </Link>
                </span>
              ))}
            </p>
          ) : null}
        </li>
      ))}
    </ol>
  );
}
